<template>
    <div v-if="open" class="picker-overlay" @click.self="close">
      <Transition name="picker-slide">
        <div v-if="open" class="picker-sheet" role="dialog" aria-modal="true" aria-label="Add participants">
          <header class="picker-header">
            <div class="picker-header__title">
              <h2>Add participants</h2>
              <span class="picker-header__count" :class="{ 'picker-header__count--full': selected.length >= max }">
                {{ selected.length }}/{{ max }}
              </span>
            </div>
            <button type="button" class="icon-btn" aria-label="Close" @click="close">
              <svg style="min-width:24px" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
            </button>
          </header>

          <div class="picker-search">
            <svg class="picker-search__icon" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2"/><path d="M21 21l-4.3-4.3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
            <input
              v-model="query"
              type="text"
              class="picker-search__input"
              placeholder="Search people"
              autocomplete="off"
            />
            <span v-if="loading" class="picker-search__spinner" aria-hidden="true"></span>
            <button v-else-if="query" type="button" class="picker-search__clear" @click="query = ''">
              <svg style="min-width: 24px;" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
            </button>
          </div>

          <p v-if="selected.length >= max" class="picker-limit-note">
            Limit reached — remove someone to add another participant.
          </p>

          <!-- Already-selected participants stay visible even before/without a search -->
          <ul v-if="selected.length" class="picker-list picker-list--selected">
            <li v-for="user in selected" :key="'selected-' + user.uid" class="picker-row" @click="toggle(user)">
              <span class="picker-row__avatar" 
              :style="{ background: user.profilImgMini1 ? `url(${bsrImg + user.profilImgMini1}) no-repeat center / cover` : avatarColor(user.uid) }"
              >
              </span>
              <span class="picker-row__meta">
                <span class="picker-row__name">{{ user.pseudo }}</span>
              </span>
              <span class="picker-row__check picker-row__check--on">
                <svg viewBox="0 0 24 24" fill="none"><path d="M4 12l5 5L20 6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </span>
            </li>
          </ul>

          <ul class="picker-list">
            <li v-if="!query" class="picker-empty">Start typing to search people.</li>
            <li v-else-if="!loading && results.length === 0" class="picker-empty">No one matches “{{ query }}”.</li>

            <li
              v-for="user in resultsExcludingSelected"
              :key="user.uid"
              class="picker-row"
              :class="{ 'picker-row--disabled': isDisabled(user.uid) }"
              @click="toggle(user)"
            >
              <span class="picker-row__avatar" 
                :style="{ background: user.profilImgMini1 ? `url(${bsrImg + user.profilImgMini1}) no-repeat center / cover` : avatarColor(user.uid) }"
              >
              </span>
              <span class="picker-row__meta">
                <span class="picker-row__name">{{ user.pseudo }}</span>
              </span>
              <span class="picker-row__check">
              </span>
            </li>
          </ul>

          <footer class="picker-footer">
            <button type="button" class="btn btn--ghost" @click="close">Cancel</button>
            <button type="button" class="btn btn--primary" @click="confirm">
              Done · {{ selected.length }} selected
            </button>
          </footer>
        </div>
      </Transition>
    </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, computed, watch, inject, onBeforeUnmount } from 'vue'
import { useStore } from "vuex";

const store = useStore();

const bsrImg = store.state.baseUrl + "images/";

const axios = inject('axios')

const props = defineProps({
  open: { type: Boolean, default: false },
  // Array of { uid, pseudo } already selected (e.g. when editing a publication).
  modelValue: {
    type: Array,
    default: () => []
  },
  max: {
    type: Number,
    default: 10
  }
})

const emit = defineEmits(['update:modelValue', 'close'])

const query = ref('')
const results = ref([])       // empty until the user searches — requirement 1
const loading = ref(false)
const selected = ref([...props.modelValue])
let debounceTimer = null

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      selected.value = [...props.modelValue]
      query.value = ''
      results.value = []
    }
  }
)

watch(query, (val) => {
  clearTimeout(debounceTimer)
  const term = val.trim()

  if (!term) {
    results.value = []
    loading.value = false
    return
  }

  loading.value = true
  debounceTimer = setTimeout(() => searchUsers(term), 400)
})

async function searchUsers(term) {
  try {
    const res = await axios.post('user.php', { type: 'searchUser', search: term })
    // Expected shape: [{ uid, pseudo }, ...]
    results.value = res.data || []
  } catch (err) {
    console.error('User search failed:', err)
    results.value = []
  } finally {
    loading.value = false
  }
}

onBeforeUnmount(() => clearTimeout(debounceTimer))

const resultsExcludingSelected = computed(() =>
  results.value.filter((u) => !selected.value.some((s) => s.uid === u.uid))
)

function isSelected(uid) {
  return selected.value.some((u) => u.uid === uid)
}

function isDisabled(uid) {
  return !isSelected(uid) && selected.value.length >= props.max
}

function toggle(user) {
  if (isSelected(user.uid)) {
    selected.value = selected.value.filter((u) => u.uid !== user.uid)
  } else if (selected.value.length < props.max) {
    selected.value = [...selected.value, user]
  }
}

function close() {
  emit('close')
}

function confirm() {
  emit('update:modelValue', selected.value)
  emit('close')
}

/* ---------- Avatar helpers ---------- */
const avatarPalette = [
  'linear-gradient(135deg,#38bdf8,#6366f1)',
  'linear-gradient(135deg,#ec4899,#8b5cf6)',
  'linear-gradient(135deg,#22d3ee,#0ea5e9)',
  'linear-gradient(135deg,#f97316,#ec4899)',
  'linear-gradient(135deg,#a78bfa,#38bdf8)'
]

function hashString(str = '') {
  let h = 0
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i)
    h |= 0
  }
  return Math.abs(h)
}

function avatarColor(uid) {
  return avatarPalette[hashString(uid) % avatarPalette.length]
}

</script>

<style scoped>
.picker-overlay {
  position: fixed;
  left:0px;
  top: 0px;
  width: 100%;
  height:100%;
  inset: 0;
  z-index: 100;
  background: rgba(6, 9, 18, 0.3);
/*   backdrop-filter: blur(2px); */
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index:2000;
}

.picker-sheet {
  --bg-card: #10162a;
  --border: rgba(148, 163, 184, 0.1);
  --cyan: #22d3ee;
  --text-primary: #e7ebf5;
  --text-secondary: #8592ad;
  --text-muted: #4b5670;

  width: 100%;
  max-width: 420px;
  max-height: 82vh;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-bottom: none;
  border-radius: 20px 20px 0 0;
  font-family: 'Inter', 'Sora', system-ui, -apple-system, sans-serif;
  color: var(--text-primary);
  overflow: hidden;
}

.picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 12px;
  border-bottom: 1px solid var(--border);
}

.picker-header__title {
  display: flex;
  align-items: baseline;
}

.picker-header__title h2 {
  margin: 0;
  margin-right: 8px;
  font-size: 16px;
  font-weight: 700;
  font-family: 'Sora', 'Inter', sans-serif;
}

.picker-header__count {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-muted);
}

.picker-header__count--full {
  color: #fb3d68;
}

.icon-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid var(--border);
  background: rgba(148, 163, 184, 0.06);
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0px;
}

.icon-btn svg {
  width: 14px;
  height: 14px;
}

.picker-search {
  position: relative;
  margin: 12px 16px 0;
  display: flex;
  align-items: center;
  background: rgba(148, 163, 184, 0.06);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 0 10px;
}

.picker-search__icon {
  width: 16px;
  height: 16px;
  margin-right: 8px;
  color: var(--text-muted);
  flex-shrink: 0;
}

.picker-search__input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 14.5px;
  padding: 11px 0;
  font-family: inherit;
}

.picker-search__input::placeholder {
  color: var(--text-muted);
}

.picker-search__clear {
  width: 26px;
  height: 26px;
  border: none;
  background: rgba(148, 163, 184, 0.12);
  border-radius: 50%;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0px;
}

.picker-search__clear svg {
  width: 12px;
  height: 12px;
}

.picker-search__spinner {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  border: 2px solid rgba(148, 163, 184, 0.25);
  border-top-color: var(--cyan);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.picker-limit-note {
  margin: 10px 16px 0;
  font-size: 12px;
  color: #fb3d68;
}

.picker-list {
  list-style: none;
  margin: 8px 0 0;
  padding: 4px 8px 8px;
  overflow-y: auto;
}

.picker-list--selected {
  flex-shrink: 0;
  max-height: 40%;
  border-bottom: 1px solid var(--border);
  padding-bottom: 8px;
}

.picker-empty {
  padding: 24px 12px;
  text-align: center;
  color: var(--text-muted);
  font-size: 13.5px;
}

.picker-row {
  display: flex;
  align-items: center;
  padding: 9px 8px;
  border-radius: 12px;
  cursor: pointer;
}

.picker-row:hover {
  background: rgba(148, 163, 184, 0.06);
}

.picker-row--disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.picker-row--disabled:hover {
  background: transparent;
}

.picker-row__avatar {
  width: 36px;
  height: 36px;
  margin-right: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
}

.picker-row__meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.picker-row__name {
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.picker-row__check {
  width: 22px;
  height: 22px;
  margin-left: 10px;
  border-radius: 50%;
  border: 1.5px solid var(--border);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.picker-row__check--on {
  background: linear-gradient(135deg, #22d3ee, #6366f1);
  border-color: transparent;
}

.picker-row__check svg {
  width: 12px;
  height: 12px;
}

.picker-footer {
  display: flex;
  padding: 12px 16px 18px;
  border-top: 1px solid var(--border);
}

.btn {
  flex: 1;
  border: none;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
}

.btn--ghost {
  margin-right: 10px;
  background: rgba(148, 163, 184, 0.08);
  color: var(--text-secondary);
}

.btn--primary {
  background: linear-gradient(135deg, #22d3ee, #6366f1);
  color: #fff;
}

/* ---- Transitions ---- */

.picker-slide-enter-active,
.picker-slide-leave-active {
  transition: transform 0.24s ease;
}
.picker-slide-enter-from,
.picker-slide-leave-to {
  transform: translateY(100%);
}

@media (max-width: 340px) {
  .picker-header__title h2 { font-size: 14.5px; }
  .picker-row__name { font-size: 13px; }
  .picker-row__avatar { width: 32px; height: 32px; font-size: 11.5px; }
}
</style>